// File: src/pages/targets/SalesManagerTarget.jsx

import React, { useEffect, useMemo, useState } from "react";
import axios from "../../import/axiosConfig";
import Swal from "sweetalert2";
import { View_User_By_Id2 } from "../../import/ApiEndPoint/Api";
import { CreateCustomerList } from "../../import/ApiEndPoint/ApiCalling";

const API = {
  list: "/target/yearly-target/list",
  update: "/target/yearly-target/update",
};

const LS_PREFIX = "SALES_MANAGER_TARGET_PAGE_V1";

const FY_MONTHS = [
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
  "January",
  "February",
  "March",
];

const CALENDAR_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const arr = (v) => (Array.isArray(v) ? v : []);
const str = (v) => (v === null || v === undefined ? "" : String(v));
const norm = (v) => str(v).trim().toLowerCase();

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const r2 = (v) => Number(num(v).toFixed(2));
const money = (v) => `₹ ${Number(num(v)).toFixed(2)}`;
const getUser = () => JSON.parse(localStorage.getItem("userData") || "{}");

const first = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
};

const safeReadLS = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const safeWriteLS = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
};

const targetsCacheKey = (db) => `${LS_PREFIX}:targets:${db || "NO_DB"}`;
const peopleCacheKey = (db) => `${LS_PREFIX}:people:${db || "NO_DB"}`;

const getCurrentFY = () => {
  const d = new Date();
  const y = d.getFullYear();

  return d.getMonth() >= 3
    ? `${y}-${String((y + 1) % 100).padStart(2, "0")}`
    : `${y - 1}-${String(y % 100).padStart(2, "0")}`;
};

const getFYStartYear = (fyLabel) => {
  const n = Number(String(fyLabel || "").split("-")[0]);
  return Number.isFinite(n) ? n : 0;
};

const getCurrentFYMonthIndex = () => {
  const currentMonthName = CALENDAR_MONTHS[new Date().getMonth()];
  return FY_MONTHS.indexOf(currentMonthName);
};

const isMonthCompleted = (fyLabel, monthName) => {
  const targetFyStart = getFYStartYear(fyLabel);
  const currentFyStart = getFYStartYear(getCurrentFY());
  const monthIndex = FY_MONTHS.indexOf(str(monthName).trim());
  const currentMonthIndex = getCurrentFYMonthIndex();

  if (!targetFyStart || monthIndex < 0) return true;
  if (targetFyStart < currentFyStart) return true;
  if (targetFyStart > currentFyStart) return false;

  return monthIndex < currentMonthIndex;
};

const getFYOptions = () => {
  const start = Number(getCurrentFY().split("-")[0]);
  const list = [];

  for (let y = start - 3; y <= start + 3; y += 1) {
    list.push(`${y}-${String((y + 1) % 100).padStart(2, "0")}`);
  }

  return list.reverse();
};

const getSavedProducts = (target = {}) =>
  arr(target.productTargets?.length ? target.productTargets : target.products);

const getMonthProducts = (monthRow = {}) =>
  arr(
    monthRow.productTargets?.length
      ? monthRow.productTargets
      : monthRow.products,
  );

const getSavedProductId = (product = {}) =>
  str(
    first(
      product.productId,
      product.ProductId,
      product._id,
      product.id,
      product.sId,
      product.productCode,
      product.Product_Code,
      product.productName,
    ),
  );

const makeFallbackMonth = (target, month, index) => {
  const products = getSavedProducts(target).map((row) => {
    const secondaryQty = r2(num(first(row.secondaryQty, row.sQty)) / 12);
    const primaryQty = r2(
      num(first(row.primaryQty, row.pQty, row.qty, row.qtyAssign)) / 12,
    );
    const total = r2(
      num(first(row.total, row.totalPrice, row.targetAmount)) / 12,
    );

    return {
      ...row,
      secondaryQty,
      sQty: secondaryQty,
      primaryQty,
      pQty: primaryQty,
      qty: primaryQty,
      qtyAssign: primaryQty,
      total,
      totalPrice: total,
      targetAmount: total,
    };
  });

  return {
    month,
    monthIndex: index,
    targetTotal: r2(num(target.grandTotal) / 12),
    totalSecondaryQty: r2(
      products.reduce((sum, p) => sum + num(first(p.secondaryQty, p.sQty)), 0),
    ),
    totalPrimaryQty: r2(
      products.reduce(
        (sum, p) => sum + num(first(p.primaryQty, p.pQty, p.qty)),
        0,
      ),
    ),
    productTargets: products,
    products,
    isEdited: false,
    isCustomerEdited: false,
  };
};

const getMonthlyTargets = (target = {}) => {
  const rows = arr(target.monthlyTargets);

  return FY_MONTHS.map((month, index) => {
    const found = rows.find((row) => norm(row.month) === norm(month));

    if (!found) return makeFallbackMonth(target, month, index);

    const products = getMonthProducts(found);

    return {
      ...found,
      month,
      monthIndex: index,
      targetTotal: r2(num(found.targetTotal)),
      totalSecondaryQty: r2(
        products.reduce(
          (sum, p) => sum + num(first(p.secondaryQty, p.sQty)),
          0,
        ),
      ),
      totalPrimaryQty: r2(
        products.reduce(
          (sum, p) => sum + num(first(p.primaryQty, p.pQty, p.qty)),
          0,
        ),
      ),
      productTargets: products,
      products,
      isEdited: Boolean(found.isEdited),
      isCustomerEdited: Boolean(found.isCustomerEdited),
    };
  });
};

const scaleTargetProduct = (product = {}, divisor = 1) => {
  const safeDivisor = num(divisor) > 0 ? num(divisor) : 1;

  const secondaryQty = r2(
    num(first(product.secondaryQty, product.sQty)) / safeDivisor,
  );

  const primaryQty = r2(
    num(
      first(product.primaryQty, product.pQty, product.qty, product.qtyAssign),
    ) / safeDivisor,
  );

  const total = r2(
    num(first(product.total, product.totalPrice, product.targetAmount)) /
      safeDivisor,
  );

  return {
    ...product,
    secondaryQty,
    sQty: secondaryQty,
    primaryQty,
    pQty: primaryQty,
    qty: primaryQty,
    qtyAssign: primaryQty,
    total,
    totalPrice: total,
    targetAmount: total,
  };
};

const makeCustomerMonthFromYearMonth = (monthRow = {}, divisor = 1) => {
  const products = getMonthProducts(monthRow).map((product) =>
    scaleTargetProduct(product, divisor),
  );

  const targetTotal = r2(
    products.reduce(
      (sum, product) =>
        sum +
        num(first(product.total, product.totalPrice, product.targetAmount)),
      0,
    ),
  );

  return {
    ...monthRow,
    targetTotal:
      targetTotal || r2(num(monthRow.targetTotal) / (num(divisor) || 1)),
    totalSecondaryQty: r2(
      products.reduce((sum, p) => sum + num(first(p.secondaryQty, p.sQty)), 0),
    ),
    totalPrimaryQty: r2(
      products.reduce(
        (sum, p) => sum + num(first(p.primaryQty, p.pQty, p.qty)),
        0,
      ),
    ),
    productTargets: products,
    products,
    isEdited: false,
    isCustomerEdited: false,
  };
};

const aggregateProductsFromMonths = (monthlyTargets = []) => {
  const map = new Map();

  arr(monthlyTargets).forEach((monthRow) => {
    getMonthProducts(monthRow).forEach((product) => {
      const key =
        getSavedProductId(product) || str(product.productName || product.name);

      if (!key) return;

      if (!map.has(key)) {
        map.set(key, {
          ...product,
          productId: str(
            first(product.productId, product._id, product.id, key),
          ),
          secondaryQty: 0,
          sQty: 0,
          primaryQty: 0,
          pQty: 0,
          qty: 0,
          qtyAssign: 0,
          total: 0,
          totalPrice: 0,
          targetAmount: 0,
        });
      }

      const row = map.get(key);

      row.secondaryQty += num(first(product.secondaryQty, product.sQty));
      row.sQty = row.secondaryQty;

      row.primaryQty += num(
        first(product.primaryQty, product.pQty, product.qty, product.qtyAssign),
      );
      row.pQty = row.primaryQty;
      row.qty = row.primaryQty;
      row.qtyAssign = row.primaryQty;

      row.total += num(
        first(product.total, product.totalPrice, product.targetAmount),
      );
      row.totalPrice = row.total;
      row.targetAmount = row.total;
    });
  });

  return Array.from(map.values()).map((product) => ({
    ...product,
    secondaryQty: r2(product.secondaryQty),
    sQty: r2(product.sQty),
    primaryQty: r2(product.primaryQty),
    pQty: r2(product.pQty),
    qty: r2(product.qty),
    qtyAssign: r2(product.qtyAssign),
    total: r2(product.total),
    totalPrice: r2(product.totalPrice),
    targetAmount: r2(product.targetAmount),
  }));
};

const getPossibleIds = (row) => {
  const values = [
    row?._id,
    row?.id,
    row?.userId,
    row?.customerId,
    row?.partyId,
    row?.sId,
    row?.employeeId,
    row?.customerCode,
    row?.code,
  ];

  if (typeof row?.partyId === "object") {
    values.push(row?.partyId?._id, row?.partyId?.id);
  }

  if (typeof row?.userId === "object") {
    values.push(row?.userId?._id, row?.userId?.id);
  }

  return values
    .map(str)
    .map((x) => x.trim())
    .filter(Boolean);
};

const getMainId = (row) => getPossibleIds(row)[0] || "";

const getCustomerTargetMainId = (target = {}) =>
  str(
    first(
      target.customerId,
      target.customer_id,
      target.partyId,
      target.customer?._id,
      target.customer?.id,
      target.customerCode,
    ),
  );

const getDisplayName = (row, fallback = "User") =>
  row?.firstName ||
  row?.fullName ||
  row?.name ||
  row?.userName ||
  row?.CompanyName ||
  row?.companyName ||
  row?.ownerName ||
  row?.email ||
  row?.mobileNumber ||
  fallback;

const getCustomerName = (row) =>
  row?.CompanyName ||
  row?.companyName ||
  row?.ownerName ||
  row?.firstName ||
  row?.name ||
  "Customer";

const extractUserListFromResponse = (data) => {
  if (Array.isArray(data?.adminDetails)) return data.adminDetails;
  if (Array.isArray(data?.User)) return data.User;
  if (Array.isArray(data?.users)) return data.users;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.result)) return data.result;
  if (Array.isArray(data)) return data;
  return [];
};

const buildUserApiPath = (uid, db) => {
  if (typeof View_User_By_Id2 === "string" && View_User_By_Id2.trim()) {
    return `${View_User_By_Id2.replace(/\/+$/, "")}/${uid}/${db}`;
  }

  return `/user/view-user-by-id/${uid}/${db}`;
};

const getRolePosition = (role) => {
  const value =
    role?.rolePosition ??
    role?.position ??
    role?.rank ??
    role?.assign ??
    role?.rolename?.rolePosition ??
    role?.rolename?.position ??
    role?.rolename?.rank ??
    0;

  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const getRoleName = (role) =>
  role?.roleName ||
  role?.name ||
  role?.rolename?.roleName ||
  role?.rolename?.name ||
  role?.id ||
  "";

const getPersonRole = (person) =>
  person?.rolename || person?.roleName || person?.role || {};

const roleMatches = (person, role) => {
  if (!person || !role) return false;

  const personRole = getPersonRole(person);
  const personRoleId = norm(personRole?._id || personRole?.id);
  const roleId = norm(role?._id || role?.id);
  const personRoleName = norm(getRoleName(personRole));
  const roleName = norm(getRoleName(role));
  const personPosition = getRolePosition(personRole);
  const rolePosition = getRolePosition(role);

  if (personRoleId && roleId && personRoleId === roleId) return true;
  if (personRoleName && roleName && personRoleName === roleName) return true;
  if (personPosition && rolePosition && personPosition === rolePosition) {
    return true;
  }

  return false;
};

const isSalesPersonUser = (user = {}, salesPersonRole = null) => {
  if (salesPersonRole && roleMatches(user, salesPersonRole)) return true;

  const personRole = getPersonRole(user);
  const roleName = norm(getRoleName(personRole));
  const pos = getRolePosition(personRole);

  if (pos === 2) return true;
  if (roleName.includes("sales person")) return true;
  if (roleName.includes("salesperson")) return true;
  if (roleName.includes("sales executive")) return true;
  if (roleName.includes("sales officer")) return true;
  if (roleName.includes("sales representative")) return true;

  if (
    roleName.includes("sales") &&
    !roleName.includes("manager") &&
    !roleName.includes("head") &&
    !roleName.includes("admin")
  ) {
    return true;
  }

  return false;
};

const isSalesManagerUser = (user = {}, salesManagerRole = null) => {
  if (salesManagerRole && roleMatches(user, salesManagerRole)) return true;

  const personRole = getPersonRole(user);
  const roleName = norm(getRoleName(personRole));
  const pos = getRolePosition(personRole);

  if (pos === 1) return true;
  if (roleName.includes("sales manager")) return true;
  if (roleName.includes("manager") && roleName.includes("sales")) return true;

  return false;
};

const extractIdFromValue = (value) => {
  if (!value) return "";

  if (typeof value === "object") {
    return str(
      value?._id ||
        value?.userId ||
        value?.customerId ||
        value?.id ||
        value?.sId ||
        "",
    );
  }

  return str(value);
};

const getAssignedSalesPersonIds = (customer = {}) => {
  const fields = [
    customer?.created_by,
    customer?.createdBy,
    customer?.assigned_to,
    customer?.assignedTo,
    customer?.assignTo,
    customer?.assignedUser,
    customer?.assignedUsers,
    customer?.assignUser,
    customer?.assignUsers,
    customer?.salesPersonId,
    customer?.salespersonId,
    customer?.salesPerson,
    customer?.salesperson,
    customer?.selectedSalesPerson,
    customer?.selectedSalesperson,
    customer?.userId,
    customer?.user,
    customer?.parentId,
    customer?.parentUserId,
    customer?.parentUser,
    customer?.parent,
    customer?.reportingTo,
    customer?.reportingManager,
  ];

  const ids = [];

  fields.forEach((field) => {
    if (!field) return;

    if (Array.isArray(field)) {
      field.forEach((item) => {
        const id = extractIdFromValue(item);
        if (id) ids.push(id);
      });
      return;
    }

    const id = extractIdFromValue(field);
    if (id) ids.push(id);
  });

  return Array.from(new Set(ids.filter(Boolean)));
};

const getAssignedManagerIds = (salesPerson = {}) => {
  const fields = [
    salesPerson?.parent,
    salesPerson?.parentId,
    salesPerson?.parentUser,
    salesPerson?.parentUserId,
    salesPerson?.manager,
    salesPerson?.managerId,
    salesPerson?.managerUser,
    salesPerson?.managerUserId,
    salesPerson?.reportingTo,
    salesPerson?.reportingManager,
    salesPerson?.assigned_to,
    salesPerson?.assignedTo,
    salesPerson?.assignTo,
    salesPerson?.created_by,
    salesPerson?.createdBy,
  ];

  const ids = [];

  fields.forEach((field) => {
    if (!field) return;

    if (Array.isArray(field)) {
      field.forEach((item) => {
        const id = extractIdFromValue(item);
        if (id) ids.push(id);
      });
      return;
    }

    const id = extractIdFromValue(field);
    if (id) ids.push(id);
  });

  return Array.from(new Set(ids.filter(Boolean)));
};

const isCustomerAssignedToSalesPerson = (customer, salesPerson) => {
  const spIds = getPossibleIds(salesPerson);
  const assignedIds = getAssignedSalesPersonIds(customer);

  return assignedIds.some((id) => spIds.includes(id));
};

const isSalesPersonAssignedToManager = (salesPerson, manager) => {
  const managerIds = getPossibleIds(manager);
  const assignedManagerIds = getAssignedManagerIds(salesPerson);

  return assignedManagerIds.some((id) => managerIds.includes(id));
};

const isDebtorCustomer = (customer = {}) => {
  const status = norm(customer?.status);

  if (status && status !== "active") return false;

  const fields = [
    customer?.partyType,
    customer?.customerType,
    customer?.type,
    customer?.Type,
    customer?.accountType,
    customer?.nature,
    customer?.category?.groupName,
    customer?.registrationType,
    customer?.groupName,
    customer?.ledgerGroup,
    customer?.underGroup,
    customer?.accountGroup,
  ]
    .map(norm)
    .filter(Boolean);

  const blockWords = [
    "purchase",
    "purchase only",
    "supplier",
    "vendor",
    "creditor",
    "sundry creditor",
    "sundry creditors",
    "payable",
  ];

  if (
    fields.some((value) =>
      blockWords.some((word) => value === word || value.includes(word)),
    )
  ) {
    return false;
  }

  return true;
};

const makeGeneratedCustomerTarget = (
  customer = {},
  yearlyTarget = {},
  customerCount = 1,
) => {
  const divisor = num(customerCount) > 0 ? num(customerCount) : 1;

  const monthlyTargets = getMonthlyTargets(yearlyTarget).map((monthRow) =>
    makeCustomerMonthFromYearMonth(monthRow, divisor),
  );

  const productTargets = aggregateProductsFromMonths(monthlyTargets);

  const yearlyTotal = r2(
    monthlyTargets.reduce(
      (sum, monthRow) => sum + num(monthRow.targetTotal),
      0,
    ),
  );

  return {
    customerId: getMainId(customer),
    customerIds: getPossibleIds(customer),
    customerName: getCustomerName(customer),
    yearlyTotal,
    targetTotal: yearlyTotal,
    grandTotal: yearlyTotal,
    productTargets,
    products: productTargets,
    monthlyTargets,
    generated: true,
    isEdited: false,
  };
};

const makeEmptyCustomerTarget = (customer = {}) => {
  const monthlyTargets = FY_MONTHS.map((month, index) => ({
    month,
    monthIndex: index,
    targetTotal: 0,
    totalSecondaryQty: 0,
    totalPrimaryQty: 0,
    productTargets: [],
    products: [],
    isEdited: false,
    isCustomerEdited: false,
  }));

  return {
    customerId: getMainId(customer),
    customerIds: getPossibleIds(customer),
    customerName: getCustomerName(customer),
    yearlyTotal: 0,
    targetTotal: 0,
    grandTotal: 0,
    productTargets: [],
    products: [],
    monthlyTargets,
    generated: true,
    isEdited: false,
  };
};

const findStoredCustomerTarget = (storedRows = [], customer = {}) => {
  const customerIds = getPossibleIds(customer);
  const customerName = norm(getCustomerName(customer));

  return (
    arr(storedRows).find((target) => {
      const storedId = getCustomerTargetMainId(target);

      if (storedId && customerIds.includes(storedId)) return true;

      const storedIds = arr(target.customerIds).map(str);
      if (storedIds.some((id) => customerIds.includes(id))) return true;

      if (customerName && norm(target.customerName) === customerName) {
        return true;
      }

      return false;
    }) || null
  );
};

const normalizeStoredCustomerTarget = (customer = {}, storedTarget = {}) => {
  const monthlyTargets = FY_MONTHS.map((month, index) => {
    const storedMonth = arr(storedTarget.monthlyTargets).find(
      (m) => norm(m.month) === norm(month),
    );

    if (!storedMonth) {
      return {
        month,
        monthIndex: index,
        targetTotal: 0,
        totalSecondaryQty: 0,
        totalPrimaryQty: 0,
        productTargets: [],
        products: [],
        isEdited: false,
        isCustomerEdited: false,
      };
    }

    const products = getMonthProducts(storedMonth);

    return {
      ...storedMonth,
      month,
      monthIndex: index,
      targetTotal: r2(num(storedMonth.targetTotal)),
      totalSecondaryQty: r2(
        products.reduce(
          (sum, p) => sum + num(first(p.secondaryQty, p.sQty)),
          0,
        ),
      ),
      totalPrimaryQty: r2(
        products.reduce(
          (sum, p) => sum + num(first(p.primaryQty, p.pQty, p.qty)),
          0,
        ),
      ),
      productTargets: products,
      products,
      isEdited: Boolean(storedMonth.isEdited || storedMonth.isCustomerEdited),
      isCustomerEdited: Boolean(
        storedMonth.isEdited || storedMonth.isCustomerEdited,
      ),
    };
  });

  const productTargets = aggregateProductsFromMonths(monthlyTargets);

  const yearlyTotal = r2(
    monthlyTargets.reduce(
      (sum, monthRow) => sum + num(monthRow.targetTotal),
      0,
    ),
  );

  return {
    ...storedTarget,
    customerId: getMainId(customer),
    customerIds: getPossibleIds(customer),
    customerName: getCustomerName(customer),
    yearlyTotal,
    targetTotal: yearlyTotal,
    grandTotal: yearlyTotal,
    productTargets,
    products: productTargets,
    monthlyTargets,
    generated: false,
  };
};

const makeStableCustomerTargetList = (
  debtorCustomers = [],
  currentFYTarget = {},
) => {
  const storedRows = arr(currentFYTarget.customerTargets);

  if (storedRows.length > 0 || currentFYTarget.customerSplitLocked) {
    return debtorCustomers.map((customer) => {
      const stored = findStoredCustomerTarget(storedRows, customer);

      if (stored) return normalizeStoredCustomerTarget(customer, stored);

      return makeEmptyCustomerTarget(customer);
    });
  }

  const customerCount = debtorCustomers.length || 1;

  return debtorCustomers.map((customer) =>
    makeGeneratedCustomerTarget(customer, currentFYTarget, customerCount),
  );
};

const makeRollupFromSources = (sources = []) => {
  const monthlyTargets = FY_MONTHS.map((month, index) => {
    const sourceMonths = arr(sources)
      .map((source) =>
        arr(source.monthlyTargets).find(
          (monthRow) => norm(monthRow.month) === norm(month),
        ),
      )
      .filter(Boolean);

    const products = aggregateProductsFromMonths(sourceMonths);

    const targetTotal = r2(
      sourceMonths.reduce(
        (sum, monthRow) => sum + num(monthRow.targetTotal),
        0,
      ),
    );

    return {
      month,
      monthIndex: index,
      targetTotal,
      totalSecondaryQty: r2(
        products.reduce(
          (sum, p) => sum + num(first(p.secondaryQty, p.sQty)),
          0,
        ),
      ),
      totalPrimaryQty: r2(
        products.reduce(
          (sum, p) => sum + num(first(p.primaryQty, p.pQty, p.qty)),
          0,
        ),
      ),
      productTargets: products,
      products,
      sourceCount: sources.length,
      isEdited: sourceMonths.some(
        (monthRow) => monthRow.isEdited || monthRow.isCustomerEdited,
      ),
      isCustomerEdited: sourceMonths.some(
        (monthRow) => monthRow.isEdited || monthRow.isCustomerEdited,
      ),
    };
  });

  const productTargets = aggregateProductsFromMonths(monthlyTargets);

  const yearlyTotal = r2(
    monthlyTargets.reduce(
      (sum, monthRow) => sum + num(monthRow.targetTotal),
      0,
    ),
  );

  return {
    yearlyTotal,
    targetTotal: yearlyTotal,
    grandTotal: yearlyTotal,
    productTargets,
    products: productTargets,
    monthlyTargets,
  };
};

const upsertYearlyTarget = (list, target) => {
  if (!target) return arr(list);

  let found = false;
  const targetId = str(target._id);
  const targetFY = str(target.fyear);

  const next = arr(list).map((item) => {
    const sameId = targetId && str(item._id) === targetId;
    const sameFY = targetFY && str(item.fyear) === targetFY;

    if (sameId || sameFY) {
      found = true;
      return { ...item, ...target };
    }

    return item;
  });

  if (!found) next.unshift(target);

  return next;
};

const makeSalesPersonTargetPayload = (target = {}) => ({
  salesPersonId: target.salesPersonId,
  salesPersonIds: arr(target.salesPersonIds),
  salesPersonName: target.salesPersonName,
  customerCount: num(target.customerCount),
  assignedCustomers: arr(target.assignedCustomers).map((customer) => ({
    customerId: getMainId(customer),
    customerIds: getPossibleIds(customer),
    customerName: getCustomerName(customer),
  })),
  assignedCustomerTargetIds: arr(target.assignedCustomerTargetIds).map(str),
  yearlyTotal: Number(target.yearlyTotal || 0),
  targetTotal: Number(target.targetTotal || 0),
  grandTotal: Number(target.grandTotal || 0),
  productTargets: arr(target.productTargets),
  products: arr(target.products),
  monthlyTargets: arr(target.monthlyTargets),
  updatedAt: new Date().toISOString(),
});

const makeSalesManagerTargetPayload = (target = {}) => ({
  salesManagerId: target.salesManagerId,
  salesManagerIds: arr(target.salesManagerIds),
  salesManagerName: target.salesManagerName,
  salesPersonCount: num(target.salesPersonCount),
  assignedSalesPersons: arr(target.assignedSalesPersons).map((salesPerson) => ({
    salesPersonId: getMainId(salesPerson),
    salesPersonIds: getPossibleIds(salesPerson),
    salesPersonName: getDisplayName(salesPerson, "Sales Person"),
  })),
  assignedSalesPersonTargetIds: arr(target.assignedSalesPersonTargetIds).map(
    str,
  ),
  yearlyTotal: Number(target.yearlyTotal || 0),
  targetTotal: Number(target.targetTotal || 0),
  grandTotal: Number(target.grandTotal || 0),
  productTargets: arr(target.productTargets),
  products: arr(target.products),
  monthlyTargets: arr(target.monthlyTargets),
  updatedAt: new Date().toISOString(),
});

const injectOnce = () => {
  const old = document.getElementById("sales-manager-target-page-css");
  if (old) old.remove();

  const style = document.createElement("style");
  style.id = "sales-manager-target-page-css";

  style.textContent = `
    .smt-page{height:100vh;width:100%;overflow:hidden;background:#f8fafc;color:#0f172a;display:flex;flex-direction:column;padding:0;margin:0;font-family:Inter,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}
    .smt-top{min-height:42px;padding:5px 7px;display:flex;align-items:center;justify-content:space-between;gap:7px;background:linear-gradient(135deg,#0f172a,#1d4ed8);color:#fff;flex:0 0 auto}
    .smt-title{display:flex;flex-direction:column;gap:1px;min-width:170px}
    .smt-title h2{margin:0;font-size:15px;line-height:1;font-weight:950;white-space:nowrap}
    .smt-title span{font-size:9px;color:rgba(255,255,255,.72);font-weight:800;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:840px}
    .smt-actions{display:flex;align-items:center;gap:5px;flex-wrap:wrap;justify-content:flex-end}
    .smt-btn{height:30px;border:none;border-radius:8px;padding:0 9px;font-size:11px;font-weight:950;cursor:pointer;white-space:nowrap;transition:.12s ease}
    .smt-btn:hover{transform:translateY(-1px)}
    .smt-btn.primary{background:#16a34a;color:#fff}
    .smt-btn.light{background:#fff;color:#0f172a}
    .smt-btn.blue{background:#2563eb;color:#fff}
    .smt-btn:disabled{opacity:.55;cursor:not-allowed;transform:none}
    .smt-filter{display:grid;grid-template-columns:120px minmax(220px,1fr) 170px 170px 170px 170px;gap:6px;align-items:end;padding:6px 7px;background:#fff;border-bottom:1px solid #dbe3ef;flex:0 0 auto}
    .smt-field{display:flex;flex-direction:column;gap:2px;min-width:0}
    .smt-label{font-size:9px;font-weight:950;color:#64748b;text-transform:uppercase;letter-spacing:.04em;line-height:1}
    .smt-input,.smt-select,.smt-stat{height:29px;border:1px solid #cbd5e1;border-radius:8px;padding:0 8px;background:#fff;color:#0f172a;outline:none;font-size:12px;font-weight:850;min-width:0}
    .smt-input:focus,.smt-select:focus{border-color:#2563eb;box-shadow:0 0 0 2px rgba(37,99,235,.12)}
    .smt-stat{display:flex;align-items:center;justify-content:space-between;background:#ecfdf5;border-color:#bbf7d0;color:#047857;font-weight:950;font-variant-numeric:tabular-nums;white-space:nowrap}
    .smt-main{flex:1 1 auto;min-height:0;display:grid;grid-template-columns:330px minmax(0,1fr);gap:8px;overflow:hidden;background:#f8fafc;padding:8px}
    .smt-left,.smt-right{min-height:0;overflow:auto;background:#fff;border:1px solid #e2e8f0;border-radius:13px;padding:8px}
    .smt-section-head{display:flex;align-items:flex-start;justify-content:space-between;gap:8px;margin-bottom:8px}
    .smt-view-title{margin:0;font-size:15px;font-weight:950;color:#0f172a}
    .smt-view-sub{margin:2px 0 0;font-size:11px;color:#64748b;font-weight:850}
    .smt-list{display:flex;flex-direction:column;gap:6px;margin-top:8px}
    .smt-row{width:100%;border:1px solid #e2e8f0;background:#f8fafc;border-radius:11px;padding:8px;display:flex;align-items:flex-start;justify-content:space-between;gap:8px;cursor:pointer;text-align:left;transition:.12s ease}
    .smt-row:hover{transform:translateY(-1px);border-color:#bfdbfe;box-shadow:0 8px 18px rgba(15,23,42,.06)}
    .smt-row.active{background:#eff6ff;border-color:#2563eb;box-shadow:0 8px 18px rgba(37,99,235,.10)}
    .smt-row b{display:block;font-size:12px;font-weight:950;color:#0f172a;line-height:1.15}
    .smt-row small{display:block;margin-top:3px;font-size:10px;font-weight:850;color:#64748b;line-height:1.2}
    .smt-row strong{font-size:11px;font-weight:950;color:#047857;white-space:nowrap;padding-top:2px}
    .smt-summary-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:7px;margin-bottom:8px}
    .smt-summary-box{background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:8px;min-height:54px}
    .smt-summary-box label{display:block;font-size:9px;font-weight:950;color:#64748b;text-transform:uppercase;line-height:1;margin-bottom:5px}
    .smt-summary-box b{display:block;font-size:14px;font-weight:950;color:#0f172a;line-height:1.05;font-variant-numeric:tabular-nums;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    .smt-note{border:1px solid #bfdbfe;background:#eff6ff;color:#1e40af;border-radius:10px;padding:7px 9px;font-size:11px;font-weight:900;margin-bottom:8px}
    .smt-note.warn{border-color:#fed7aa;background:#fff7ed;color:#9a3412}
    .smt-sp-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:6px;margin-bottom:8px}
    .smt-sp-card{border:1px solid #e2e8f0;background:#f8fafc;border-radius:10px;padding:7px;min-height:56px}
    .smt-sp-card b{display:block;font-size:12px;font-weight:950;color:#0f172a;line-height:1.2}
    .smt-sp-card small{display:block;margin-top:4px;font-size:10px;font-weight:850;color:#64748b}
    .smt-month-grid{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:6px;margin-bottom:8px}
    .smt-month-card{border:1px solid #e2e8f0;background:linear-gradient(180deg,#fff,#f8fafc);border-radius:11px;padding:7px;min-height:82px;cursor:pointer;transition:.15s ease;display:flex;flex-direction:column;justify-content:space-between;gap:4px;text-align:left}
    .smt-month-card:hover{transform:translateY(-1px);border-color:#93c5fd;box-shadow:0 8px 18px rgba(15,23,42,.07)}
    .smt-month-card.selected{border-color:#2563eb;box-shadow:0 0 0 2px rgba(37,99,235,.12)}
    .smt-month-card.edited{border-color:#bbf7d0;background:linear-gradient(180deg,#ecfdf5,#fff)}
    .smt-month-card.completed{background:#e5e7eb;border-color:#cbd5e1;color:#64748b;cursor:pointer;opacity:.86;box-shadow:none;transform:none}
    .smt-month-name{font-size:13px;font-weight:950;color:#0f172a;line-height:1}
    .smt-month-card.completed .smt-month-name,.smt-month-card.completed .smt-month-total{color:#64748b}
    .smt-month-total{font-size:14px;font-weight:950;color:#047857;font-variant-numeric:tabular-nums}
    .smt-month-meta{display:flex;align-items:center;justify-content:space-between;gap:5px;font-size:9px;font-weight:900;color:#64748b}
    .smt-chip{display:inline-flex;align-items:center;height:18px;padding:0 6px;border-radius:999px;font-size:8px;font-weight:950;white-space:nowrap;background:#eff6ff;border:1px solid #bfdbfe;color:#1d4ed8}
    .smt-chip.green{background:#ecfdf5;border-color:#bbf7d0;color:#047857}
    .smt-chip.gray{background:#f1f5f9;border-color:#cbd5e1;color:#64748b}
    .smt-month-view{border:1px solid #e2e8f0;border-radius:13px;background:#fff;padding:8px;margin-top:8px}
    .smt-table-wrap{width:100%;overflow:auto;border:1px solid #e2e8f0;border-radius:12px}
    .smt-view-table{width:100%;min-width:760px;border-collapse:collapse;background:#fff}
    .smt-view-table th{background:#e2e8f0;border-bottom:1px solid #cbd5e1;padding:7px;font-size:10px;text-align:left;text-transform:uppercase;font-weight:950;color:#334155}
    .smt-view-table td{border-bottom:1px solid #eef2f7;padding:7px;font-size:12px;font-weight:850;color:#0f172a}
    .smt-view-table tr.total-row td{background:#f8fafc;font-weight:950;color:#047857}
    .smt-empty{border:1px dashed #cbd5e1;background:#fff;color:#64748b;border-radius:12px;padding:18px;font-size:13px;font-weight:900;text-align:center}
    @media(max-width:1250px){.smt-filter{grid-template-columns:repeat(3,minmax(0,1fr))}.smt-main{grid-template-columns:1fr;overflow:auto}.smt-summary-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.smt-month-grid{grid-template-columns:repeat(4,minmax(0,1fr))}.smt-sp-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}
    @media(max-width:760px){.smt-page{overflow:auto;height:auto;min-height:100vh}.smt-filter{grid-template-columns:1fr}.smt-main{display:block}.smt-left,.smt-right{margin-bottom:8px}.smt-summary-grid,.smt-month-grid,.smt-sp-grid{grid-template-columns:1fr}.smt-title span{display:none}}
  `;

  document.head.appendChild(style);
};

export default function SalesManagerTarget() {
  const currentUser = getUser();
  const database = currentUser.database || currentUser.db || "";

  const [fy, setFy] = useState(getCurrentFY());
  const [savedTargets, setSavedTargets] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [loadingTargets, setLoadingTargets] = useState(false);
  const [loadingPeople, setLoadingPeople] = useState(false);
  const [savingRollup, setSavingRollup] = useState(false);

  const [salesManagerSearch, setSalesManagerSearch] = useState("");
  const [selectedSalesManagerId, setSelectedSalesManagerId] = useState("");
  const [monthView, setMonthView] = useState(null);

  useEffect(() => {
    injectOnce();
  }, []);

  useEffect(() => {
    loadSavedTargets();
    loadPeople();
  }, []);

  const currentFYTarget = useMemo(() => {
    return (
      savedTargets.find(
        (target) =>
          String(target.fyear) === String(fy) &&
          norm(target.status || "Active") !== "deleted",
      ) || null
    );
  }, [savedTargets, fy]);

  const salesPersonRole = useMemo(
    () => roles.find((role) => getRolePosition(role) === 2),
    [roles],
  );

  const salesManagerRole = useMemo(
    () => roles.find((role) => getRolePosition(role) === 1),
    [roles],
  );

  const salesPersonList = useMemo(() => {
    return arr(users)
      .filter((user) => isSalesPersonUser(user, salesPersonRole))
      .sort((a, b) =>
        String(getDisplayName(a, "")).localeCompare(
          String(getDisplayName(b, "")),
        ),
      );
  }, [users, salesPersonRole]);

  const salesManagerList = useMemo(() => {
    return arr(users)
      .filter((user) => isSalesManagerUser(user, salesManagerRole))
      .sort((a, b) =>
        String(getDisplayName(a, "")).localeCompare(
          String(getDisplayName(b, "")),
        ),
      );
  }, [users, salesManagerRole]);

  const debtorCustomers = useMemo(() => {
    return arr(customers)
      .filter(isDebtorCustomer)
      .sort((a, b) =>
        String(getCustomerName(a)).localeCompare(String(getCustomerName(b))),
      );
  }, [customers]);

  const customerTargetList = useMemo(() => {
    if (!currentFYTarget) return [];

    return makeStableCustomerTargetList(debtorCustomers, currentFYTarget).sort(
      (a, b) =>
        String(a.customerName || "").localeCompare(
          String(b.customerName || ""),
        ),
    );
  }, [currentFYTarget, debtorCustomers]);

  const customerTargetById = useMemo(() => {
    const map = new Map();

    customerTargetList.forEach((target) => {
      const ids = [getCustomerTargetMainId(target), ...arr(target.customerIds)]
        .map(str)
        .filter(Boolean);

      ids.forEach((id) => {
        if (!map.has(id)) map.set(id, target);
      });
    });

    return map;
  }, [customerTargetList]);

  const salesPersonTargetList = useMemo(() => {
    if (!currentFYTarget) return [];

    return salesPersonList
      .map((salesPerson) => {
        const assignedCustomers = debtorCustomers.filter((customer) =>
          isCustomerAssignedToSalesPerson(customer, salesPerson),
        );

        const assignedCustomerTargets = assignedCustomers
          .map((customer) => {
            const ids = getPossibleIds(customer);
            return ids.map((id) => customerTargetById.get(id)).find(Boolean);
          })
          .filter(Boolean);

        const rollup = makeRollupFromSources(assignedCustomerTargets);

        return {
          salesPersonId: getMainId(salesPerson),
          salesPersonIds: getPossibleIds(salesPerson),
          salesPersonName: getDisplayName(salesPerson, "Sales Person"),
          salesPerson,
          customerCount: assignedCustomerTargets.length,
          assignedCustomers: assignedCustomers.map((customer) => ({
            ...customer,
            customerId: getMainId(customer),
            customerIds: getPossibleIds(customer),
            customerName: getCustomerName(customer),
          })),
          assignedCustomerTargets,
          assignedCustomerTargetIds: assignedCustomerTargets.map(
            getCustomerTargetMainId,
          ),
          ...rollup,
          updatedAt: new Date().toISOString(),
        };
      })
      .sort((a, b) =>
        String(a.salesPersonName || "").localeCompare(
          String(b.salesPersonName || ""),
        ),
      );
  }, [currentFYTarget, salesPersonList, debtorCustomers, customerTargetById]);

  const salesPersonTargetById = useMemo(() => {
    const map = new Map();

    salesPersonTargetList.forEach((target) => {
      const ids = [target.salesPersonId, ...arr(target.salesPersonIds)]
        .map(str)
        .filter(Boolean);

      ids.forEach((id) => {
        if (!map.has(id)) map.set(id, target);
      });
    });

    return map;
  }, [salesPersonTargetList]);

  const salesManagerTargetList = useMemo(() => {
    if (!currentFYTarget) return [];

    return salesManagerList
      .map((manager) => {
        let assignedSalesPersons = salesPersonList.filter((salesPerson) =>
          isSalesPersonAssignedToManager(salesPerson, manager),
        );

        if (
          salesManagerList.length === 1 &&
          assignedSalesPersons.length === 0
        ) {
          assignedSalesPersons = salesPersonList;
        }

        const assignedSalesPersonTargets = assignedSalesPersons
          .map((salesPerson) => {
            const ids = getPossibleIds(salesPerson);
            return ids.map((id) => salesPersonTargetById.get(id)).find(Boolean);
          })
          .filter(Boolean);

        const rollup = makeRollupFromSources(assignedSalesPersonTargets);

        return {
          salesManagerId: getMainId(manager),
          salesManagerIds: getPossibleIds(manager),
          salesManagerName: getDisplayName(manager, "Sales Manager"),
          salesManager: manager,
          salesPersonCount: assignedSalesPersonTargets.length,
          assignedSalesPersons: assignedSalesPersons.map((salesPerson) => ({
            ...salesPerson,
            salesPersonId: getMainId(salesPerson),
            salesPersonIds: getPossibleIds(salesPerson),
            salesPersonName: getDisplayName(salesPerson, "Sales Person"),
          })),
          assignedSalesPersonTargets,
          assignedSalesPersonTargetIds: assignedSalesPersonTargets.map(
            (target) => target.salesPersonId,
          ),
          ...rollup,
          updatedAt: new Date().toISOString(),
        };
      })
      .sort((a, b) =>
        String(a.salesManagerName || "").localeCompare(
          String(b.salesManagerName || ""),
        ),
      );
  }, [
    currentFYTarget,
    salesManagerList,
    salesPersonList,
    salesPersonTargetById,
  ]);

  const filteredSalesManagerTargetList = useMemo(() => {
    const q = norm(salesManagerSearch);
    if (!q) return salesManagerTargetList;

    return salesManagerTargetList.filter((target) =>
      [
        target.salesManagerName,
        target.salesManagerId,
        arr(target.salesManagerIds).join(" "),
      ]
        .map(str)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [salesManagerTargetList, salesManagerSearch]);

  const selectedSalesManagerTarget = useMemo(() => {
    if (!salesManagerTargetList.length) return null;

    return (
      salesManagerTargetList.find(
        (target) => target.salesManagerId === selectedSalesManagerId,
      ) || salesManagerTargetList[0]
    );
  }, [salesManagerTargetList, selectedSalesManagerId]);

  useEffect(() => {
    if (!selectedSalesManagerTarget) {
      setSelectedSalesManagerId("");
      setMonthView(null);
      return;
    }

    if (
      !selectedSalesManagerId ||
      selectedSalesManagerId !== selectedSalesManagerTarget.salesManagerId
    ) {
      setSelectedSalesManagerId(selectedSalesManagerTarget.salesManagerId);
    }
  }, [selectedSalesManagerTarget, selectedSalesManagerId]);

  const selectedMonthProducts = useMemo(
    () => getMonthProducts(monthView?.monthRow),
    [monthView],
  );

  const assignedSalesPersonTotal = useMemo(() => {
    const set = new Set();

    salesManagerTargetList.forEach((managerTarget) => {
      arr(managerTarget.assignedSalesPersons).forEach((salesPerson) => {
        const id = getMainId(salesPerson);
        if (id) set.add(id);
      });
    });

    return set.size;
  }, [salesManagerTargetList]);

  const unassignedSalesPersonCount = Math.max(
    salesPersonList.length - assignedSalesPersonTotal,
    0,
  );

  const salesManagerRollupTotal = salesManagerTargetList.reduce(
    (sum, target) => sum + num(target.yearlyTotal || target.grandTotal),
    0,
  );

  const savedCustomerSplitExists =
    Boolean(currentFYTarget?.customerSplitLocked) ||
    arr(currentFYTarget?.customerTargets).length > 0;

  const loading = loadingTargets || loadingPeople;

  const loadSavedTargets = async () => {
    const db = getUser().database || getUser().db || "";

    if (!db) {
      setSavedTargets([]);
      return;
    }

    const cached = safeReadLS(targetsCacheKey(db), null);
    if (cached?.items?.length) setSavedTargets(cached.items);

    try {
      setLoadingTargets(true);

      const res = await axios.get(`${API.list}/${db}`);
      const payload = res?.data ?? res;
      const list = arr(payload.data || payload.YearlyTarget || []);

      setSavedTargets(list);

      safeWriteLS(targetsCacheKey(db), {
        updatedAt: new Date().toISOString(),
        items: list,
      });
    } catch (err) {
      console.log("[SalesManagerTarget] loadSavedTargets error:", err);
      if (!cached?.items?.length) setSavedTargets([]);
    } finally {
      setLoadingTargets(false);
    }
  };

  const loadPeople = async () => {
    const userinfo = getUser();
    const uid = userinfo?._id || userinfo?.id || "";
    const db = userinfo?.database || userinfo?.db || "";

    if (!uid || !db) {
      setRoles([]);
      setUsers([]);
      setCustomers([]);
      return;
    }

    const cached = safeReadLS(peopleCacheKey(db), null);

    if (cached?.users || cached?.customers || cached?.roles) {
      setRoles(arr(cached.roles));
      setUsers(arr(cached.users));
      setCustomers(arr(cached.customers));
    }

    try {
      setLoadingPeople(true);

      const [roleRes, userRes, customerRes] = await Promise.allSettled([
        axios.get(`/assign-role/view-assign-role/${db}`),
        axios.get(buildUserApiPath(uid, db)),
        CreateCustomerList(uid, db),
      ]);

      let nextRoles = [];

      if (roleRes.status === "fulfilled") {
        const departments = arr(roleRes?.value?.data?.Department);
        const salesDept = departments.find(
          (department) =>
            norm(department?.departmentName?.departmentName) === "sales",
        );

        nextRoles = arr(salesDept?.roles)
          .map((role) => ({
            ...role,
            rolePosition: getRolePosition(role),
            roleName: getRoleName(role),
          }))
          .filter((role) => getRoleName(role))
          .sort((a, b) => getRolePosition(a) - getRolePosition(b));
      }

      let nextUsers = [];

      if (userRes.status === "fulfilled") {
        nextUsers = extractUserListFromResponse(userRes?.value?.data).filter(
          (user) => {
            const status = norm(user?.status);
            const roleName = norm(user?.rolename?.roleName);

            if (status && status !== "active") return false;
            if (roleName === "customer") return false;

            return true;
          },
        );
      }

      let nextCustomers = [];

      if (customerRes.status === "fulfilled") {
        nextCustomers = arr(customerRes?.value?.Customer);
      }

      setRoles(nextRoles);
      setUsers(nextUsers);
      setCustomers(nextCustomers);

      safeWriteLS(peopleCacheKey(db), {
        updatedAt: new Date().toISOString(),
        roles: nextRoles,
        users: nextUsers,
        customers: nextCustomers,
      });
    } catch (err) {
      console.log("[SalesManagerTarget] loadPeople error:", err);

      if (!cached?.roles) setRoles([]);
      if (!cached?.users) setUsers([]);
      if (!cached?.customers) setCustomers([]);
    } finally {
      setLoadingPeople(false);
    }
  };

  const refreshAll = () => {
    loadSavedTargets();
    loadPeople();
  };

  const selectSalesManagerTarget = (target) => {
    setSelectedSalesManagerId(target.salesManagerId);
    setMonthView(null);
  };

  const openMonthView = (salesManagerTarget, monthRow) => {
    setMonthView({ salesManagerTarget, monthRow });
  };

  const saveSalesManagerRollup = async () => {
    try {
      if (!currentFYTarget?._id) {
        Swal.fire({
          icon: "warning",
          title: "No Yearly Target",
          text: `Please create yearly target for ${fy} first.`,
          confirmButtonColor: "#f59e0b",
        });
        return;
      }

      if (!savedCustomerSplitExists) {
        Swal.fire({
          icon: "warning",
          title: "Customer Target Not Saved",
          text: "Please open Customer Target page first and save customer-wise target. Sales manager target must come from saved customer and sales person targets.",
          confirmButtonColor: "#f59e0b",
        });
        return;
      }

      if (!salesManagerTargetList.length) {
        Swal.fire({
          icon: "warning",
          title: "No Sales Manager Found",
          text: "No sales manager found to save rollup target.",
          confirmButtonColor: "#f59e0b",
        });
        return;
      }

      setSavingRollup(true);

      const salesPersonTargets = salesPersonTargetList.map(
        makeSalesPersonTargetPayload,
      );

      const salesManagerTargets = salesManagerTargetList.map(
        makeSalesManagerTargetPayload,
      );

      const finalTarget = {
        ...currentFYTarget,
        database,
        fyear: fy,
        customerTargets: customerTargetList,
        salesPersonTargets,
        salesManagerTargets,
        updated_by: currentUser._id || currentUser.id || "",
        updatedAt: new Date().toISOString(),
      };

      const optimisticList = upsertYearlyTarget(savedTargets, finalTarget);

      setSavedTargets(optimisticList);

      safeWriteLS(targetsCacheKey(database), {
        updatedAt: new Date().toISOString(),
        items: optimisticList,
      });

      const { _id, __v, createdAt, updatedAt, ...payloadWithoutIds } =
        finalTarget;

      await axios.put(`${API.update}/${currentFYTarget._id}`, {
        ...payloadWithoutIds,
        database,
        fyear: fy,
        customerTargets: customerTargetList,
        salesPersonTargets,
        salesManagerTargets,
        updated_by: currentUser._id || currentUser.id || "",
      });

      Swal.fire({
        icon: "success",
        title: "Sales Manager Target Saved",
        text: "Sales manager targets were saved from assigned sales person targets.",
        confirmButtonColor: "#16a34a",
      });

      loadSavedTargets();
    } catch (err) {
      console.log("[SalesManagerTarget] saveSalesManagerRollup error:", err);

      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Unable to save sales manager target rollup.",
        confirmButtonColor: "#dc2626",
      });

      loadSavedTargets();
    } finally {
      setSavingRollup(false);
    }
  };

  const renderProductViewTable = (products = []) => {
    const rows = arr(products);

    const total = rows.reduce(
      (sum, product) =>
        sum +
        num(first(product.total, product.totalPrice, product.targetAmount)),
      0,
    );

    if (!rows.length) {
      return (
        <div className="smt-empty">No product target found for this month.</div>
      );
    }

    return (
      <div className="smt-table-wrap">
        <table className="smt-view-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Product</th>
              <th>Category</th>
              <th>Color</th>
              <th>Secondary Qty</th>
              <th>Primary Qty</th>
              <th>Amount</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((product, index) => (
              <tr key={`${getSavedProductId(product)}-${index}`}>
                <td>{index + 1}</td>

                <td>
                  {product.productName || product.Product_Title || product.name}
                </td>

                <td>
                  {product.category || product.catageory || "-"}
                  <div className="smt-view-sub">
                    {product.subCategory || product.SubCategory || "-"}
                  </div>
                </td>

                <td>{product.color || "-"}</td>

                <td>
                  {Number(
                    num(first(product.secondaryQty, product.sQty)),
                  ).toFixed(2)}
                </td>

                <td>
                  {Number(
                    num(first(product.primaryQty, product.pQty, product.qty)),
                  ).toFixed(2)}
                </td>

                <td>
                  {money(
                    first(
                      product.total,
                      product.totalPrice,
                      product.targetAmount,
                    ),
                  )}
                </td>
              </tr>
            ))}

            <tr className="total-row">
              <td colSpan={6}>Total</td>
              <td>{money(total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="smt-page">
      <div className="smt-top">
        <div className="smt-title">
          <h2>Sales Manager Target</h2>
          <span>
            Sales manager target is the sum of assigned sales person targets.
            Edit only customer targets; this page is roll-up view.
          </span>
        </div>

        <div className="smt-actions">
          <button type="button" className="smt-btn light" onClick={refreshAll}>
            {loading ? "Loading..." : "Refresh"}
          </button>

          <button
            type="button"
            className="smt-btn primary"
            onClick={saveSalesManagerRollup}
            disabled={savingRollup || !currentFYTarget}
          >
            {savingRollup ? "Saving..." : "Save Sales Manager Rollup"}
          </button>
        </div>
      </div>

      <div className="smt-filter">
        <div className="smt-field">
          <label className="smt-label">Financial Year</label>

          <select
            className="smt-select"
            value={fy}
            onChange={(e) => {
              setFy(e.target.value);
              setSelectedSalesManagerId("");
              setMonthView(null);
            }}
          >
            {getFYOptions().map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>

        <div className="smt-field">
          <label className="smt-label">Search Sales Manager</label>

          <input
            className="smt-input"
            value={salesManagerSearch}
            onChange={(e) => setSalesManagerSearch(e.target.value)}
            placeholder="Search sales manager"
          />
        </div>

        <div className="smt-field">
          <label className="smt-label">Sales Managers</label>
          <div className="smt-stat">
            <span>{salesManagerTargetList.length}</span>
            <b>{filteredSalesManagerTargetList.length}</b>
          </div>
        </div>

        <div className="smt-field">
          <label className="smt-label">Sales Persons</label>
          <div className="smt-stat">
            <span>Total</span>
            <b>{salesPersonList.length}</b>
          </div>
        </div>

        <div className="smt-field">
          <label className="smt-label">Unassigned SP</label>
          <div className="smt-stat">
            <span>SP</span>
            <b>{unassignedSalesPersonCount}</b>
          </div>
        </div>

        <div className="smt-field">
          <label className="smt-label">SM Rollup Total</label>
          <div className="smt-stat">
            <span>Total</span>
            <b>{money(salesManagerRollupTotal)}</b>
          </div>
        </div>
      </div>

      {!currentFYTarget ? (
        <div className="smt-main">
          <div className="smt-left">
            <div className="smt-empty">No yearly target found.</div>
          </div>

          <div className="smt-right">
            <div className="smt-empty">
              Please create yearly target for FY {fy} first.
            </div>
          </div>
        </div>
      ) : (
        <div className="smt-main">
          <div className="smt-left">
            <div className="smt-section-head">
              <div>
                <h3 className="smt-view-title">Sales Managers</h3>
                <p className="smt-view-sub">
                  Combined target from assigned sales persons.
                </p>
              </div>
            </div>

            {!savedCustomerSplitExists ? (
              <div className="smt-note warn">
                Customer-wise target is not saved yet. Open Customer Target page
                first and save customer split; then this manager roll-up will be
                final.
              </div>
            ) : (
              <div className="smt-note">
                Sales manager target is calculated from assigned sales person
                targets. This page is view-only.
              </div>
            )}

            {unassignedSalesPersonCount > 0 ? (
              <div className="smt-note warn">
                {unassignedSalesPersonCount} sales person(s) are not assigned to
                any manager, so their target may not be counted unless there is
                only one manager.
              </div>
            ) : null}

            <div className="smt-list">
              {filteredSalesManagerTargetList.map((target) => {
                const active = target.salesManagerId === selectedSalesManagerId;

                return (
                  <button
                    type="button"
                    key={target.salesManagerId}
                    className={`smt-row ${active ? "active" : ""}`}
                    onClick={() => selectSalesManagerTarget(target)}
                  >
                    <span>
                      <b>{target.salesManagerName}</b>
                      <small>
                        Assigned Sales Persons: {target.salesPersonCount || 0}
                      </small>
                      <small>
                        Products: {arr(target.productTargets).length} • Months:{" "}
                        {arr(target.monthlyTargets).length}
                      </small>
                    </span>

                    <strong>{money(target.yearlyTotal || 0)}</strong>
                  </button>
                );
              })}

              {!filteredSalesManagerTargetList.length ? (
                <div className="smt-empty">No sales manager found.</div>
              ) : null}
            </div>
          </div>

          <div className="smt-right">
            {!selectedSalesManagerTarget ? (
              <div className="smt-empty">
                Select a sales manager to view target.
              </div>
            ) : (
              <>
                <div className="smt-section-head">
                  <div>
                    <h3 className="smt-view-title">
                      {selectedSalesManagerTarget.salesManagerName}
                    </h3>

                    <p className="smt-view-sub">
                      FY: {fy} • Assigned Sales Persons:{" "}
                      {selectedSalesManagerTarget.salesPersonCount || 0}
                    </p>
                  </div>

                  <button
                    type="button"
                    className="smt-btn light"
                    onClick={() => setMonthView(null)}
                  >
                    Clear Month
                  </button>
                </div>

                <div className="smt-summary-grid">
                  <div className="smt-summary-box">
                    <label>Sales Manager Target</label>
                    <b>{money(selectedSalesManagerTarget.yearlyTotal || 0)}</b>
                  </div>

                  <div className="smt-summary-box">
                    <label>Assigned Sales Persons</label>
                    <b>{selectedSalesManagerTarget.salesPersonCount || 0}</b>
                  </div>

                  <div className="smt-summary-box">
                    <label>Products</label>
                    <b>
                      {arr(selectedSalesManagerTarget.productTargets).length}
                    </b>
                  </div>

                  <div className="smt-summary-box">
                    <label>Edited Months</label>
                    <b>
                      {
                        arr(selectedSalesManagerTarget.monthlyTargets).filter(
                          (month) => month.isEdited || month.isCustomerEdited,
                        ).length
                      }
                    </b>
                  </div>
                </div>

                <div className="smt-note">
                  This page is read-only. To change sales manager target, edit
                  customer targets or sales person assignment.
                </div>

                {arr(selectedSalesManagerTarget.assignedSalesPersonTargets)
                  .length ? (
                  <div className="smt-sp-grid">
                    {selectedSalesManagerTarget.assignedSalesPersonTargets.map(
                      (salesPersonTarget) => (
                        <div
                          className="smt-sp-card"
                          key={salesPersonTarget.salesPersonId}
                        >
                          <b>{salesPersonTarget.salesPersonName}</b>
                          <small>
                            SP Target: {money(salesPersonTarget.yearlyTotal)}
                          </small>
                          <small>
                            Customers: {salesPersonTarget.customerCount || 0}
                          </small>
                        </div>
                      ),
                    )}
                  </div>
                ) : (
                  <div className="smt-empty">
                    No sales person target found under this sales manager.
                  </div>
                )}

                <div className="smt-month-grid">
                  {arr(selectedSalesManagerTarget.monthlyTargets).map(
                    (monthRow) => {
                      const completed = isMonthCompleted(fy, monthRow.month);
                      const active =
                        norm(monthView?.monthRow?.month) ===
                        norm(monthRow.month);

                      return (
                        <button
                          type="button"
                          key={monthRow.month}
                          className={`smt-month-card ${
                            monthRow.isEdited || monthRow.isCustomerEdited
                              ? "edited"
                              : ""
                          } ${completed ? "completed" : ""} ${
                            active ? "selected" : ""
                          }`}
                          onClick={() =>
                            openMonthView(selectedSalesManagerTarget, monthRow)
                          }
                        >
                          <div>
                            <div className="smt-month-name">
                              {monthRow.month}
                            </div>

                            <div className="smt-view-sub">
                              {completed ? "Completed Month" : "Current/Future"}
                            </div>
                          </div>

                          <div className="smt-month-total">
                            {money(monthRow.targetTotal)}
                          </div>

                          <div className="smt-month-meta">
                            <span>
                              {getMonthProducts(monthRow).length} Products
                            </span>

                            <span
                              className={`smt-chip ${
                                completed
                                  ? "gray"
                                  : monthRow.isEdited ||
                                      monthRow.isCustomerEdited
                                    ? "green"
                                    : ""
                              }`}
                            >
                              {completed
                                ? "Locked"
                                : monthRow.isEdited || monthRow.isCustomerEdited
                                  ? "Updated"
                                  : "View"}
                            </span>
                          </div>
                        </button>
                      );
                    },
                  )}
                </div>

                {monthView ? (
                  <div className="smt-month-view">
                    <div className="smt-section-head">
                      <div>
                        <h3 className="smt-view-title">
                          {monthView.monthRow.month} Target -{" "}
                          {selectedSalesManagerTarget.salesManagerName}
                        </h3>

                        <p className="smt-view-sub">
                          Month Total: {money(monthView.monthRow.targetTotal)} •
                          Products: {selectedMonthProducts.length}
                        </p>
                      </div>
                    </div>

                    {renderProductViewTable(selectedMonthProducts)}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
